/**
 * F-505-FE-06 / F-603-FE-01: WebSocketContext
 *
 * Manages the WebSocket connection lifecycle:
 * - Connects automatically when authenticated (triggered by caller via connect())
 * - Disconnects on logout
 * - Exponential backoff reconnection with jitter (F-603: 100ms base, 5000ms max, +/-50% jitter)
 * - Typed message dispatch (send)
 * - Message subscription by type (onMessage / offMessage)
 * - Connection status: 'connected' | 'connecting' | 'disconnected'
 * - Reconnection state: isReconnecting flag and reconnection success/failure tracking
 *
 * On successful reconnection (F-603):
 * - Sends SESSION_REJOIN message with the current sessionId (if set).
 * - Listens for SESSION_REJOINED confirmation from the server.
 * - Tracks reconnection events for ConnectionStatus component to display UI feedback.
 *
 * The WebSocket URL is derived from the current page origin (ws: / wss:)
 * with path /ws, forwarded through the Vite proxy in dev.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WSConnectionStatus = 'connected' | 'connecting' | 'disconnected';

/** Outbound typed message envelope */
export interface WSOutboundMessage {
  type: string;
  [key: string]: unknown;
}

/** Inbound typed message envelope */
export interface WSInboundMessage {
  type: string;
  [key: string]: unknown;
}

export type MessageHandler = (message: WSInboundMessage) => void;

export interface TranscriptSegmentCatchUp {
  id: string;
  sequenceNumber: number;
  speaker: string;
  direction: string;
  originalText: string;
  translatedText: string;
  originalLang: string;
  targetLang: string;
  createdAt: string;
}

export interface WebSocketContextValue {
  status: WSConnectionStatus;
  /** True when a reconnection is in progress (was previously connected, now reconnecting). */
  isReconnecting: boolean;
  /** True for 3 seconds after a successful reconnection — drives "Reconnected" toast. */
  justReconnected: boolean;
  /** The raw WebSocket instance — needed for binary audio frame transmission. */
  rawSocket: WebSocket | null;
  /** Send a typed JSON message */
  send: (message: WSOutboundMessage) => void;
  /** Register a handler for a specific message type. Returns an unsubscribe fn. */
  onMessage: (type: string, handler: MessageHandler) => () => void;
  /** Imperatively connect (called after successful auth) */
  connect: (token: string) => void;
  /** Imperatively disconnect (called on logout) */
  disconnect: () => void;
  /** Set the active sessionId so it can be included in SESSION_REJOIN on reconnect. */
  setSessionId: (sessionId: string | null) => void;
  /** Update the last known sequence number — called by session pages as segments arrive. */
  updateLastSeqNumber: (seq: number) => void;
  /**
   * Missed segments fetched after reconnection catch-up.
   * Cleared to [] after being consumed by the session page.
   */
  missedSegments: TranscriptSegmentCatchUp[];
  /** Clear the missed segments list after the session page has consumed them. */
  clearMissedSegments: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// F-603: Reconnection parameters per spec
// Starting delay 100ms, multiplier 2x, max 5000ms, jitter +/-50%
const BASE_DELAY_MS = 100;
const MAX_DELAY_MS = 5_000;
const JITTER_FACTOR = 0.5; // +/-50% means multiply by random in [0.5, 1.5]

function getWsUrl(token: string): string {
  const base = import.meta.env.VITE_API_URL ?? '';
  if (base) {
    // Convert http(s) → ws(s)
    const wsBase = base.replace(/^http/, 'ws');
    return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
  }
  // Use current origin
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
}

/**
 * Compute exponential backoff delay with +/-50% jitter.
 * Result is in the range [delay * 0.5, delay * 1.5] where delay = BASE * 2^attempt,
 * capped at MAX_DELAY_MS before jitter is applied.
 */
function backoffDelay(attempt: number): number {
  const base = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
  // Random multiplier in [1 - JITTER_FACTOR, 1 + JITTER_FACTOR] = [0.5, 1.5]
  const multiplier = 1 - JITTER_FACTOR + Math.random() * 2 * JITTER_FACTOR;
  return Math.round(base * multiplier);
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<WSConnectionStatus>('disconnected');
  // True when a reconnection attempt is in progress (was previously connected)
  const [isReconnecting, setIsReconnecting] = useState(false);
  // True for 3 seconds after a successful reconnection
  const [justReconnected, setJustReconnected] = useState(false);
  // Raw WebSocket instance — exposed for binary audio frame transmission
  const [rawSocket, setRawSocket] = useState<WebSocket | null>(null);
  // Missed segments fetched during catch-up after reconnection
  const [missedSegments, setMissedSegments] = useState<TranscriptSegmentCatchUp[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const tokenRef = useRef<string | null>(null);
  const attemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectedToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldReconnectRef = useRef(false);
  // Track whether we have ever successfully connected (to distinguish first-connect vs reconnect)
  const hasConnectedRef = useRef(false);
  // Active sessionId — set by consuming page so SESSION_REJOIN can include it
  const sessionIdRef = useRef<string | null>(null);
  // Last known sequence number — updated as segments arrive during a session
  const lastSeqNumberRef = useRef(0);

  /** Map from message type → Set of handlers */
  const handlersRef = useRef<Map<string, Set<MessageHandler>>>(new Map());

  // -------------------------------------------------------------------------
  // Internal: open WebSocket
  // -------------------------------------------------------------------------
  const openSocket = useCallback((token: string) => {
    // Clean up any existing socket
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('connecting');
    const ws = new WebSocket(getWsUrl(token));
    wsRef.current = ws;
    setRawSocket(ws);

    // Page visibility — pause heartbeat timeout when tab is backgrounded
    const handleVisibility = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      const type = document.hidden ? 'CLIENT_BACKGROUNDED' : 'CLIENT_FOREGROUNDED';
      wsRef.current.send(JSON.stringify({ type }));
    };
    document.addEventListener('visibilitychange', handleVisibility);

    ws.onopen = () => {
      const wasReconnecting = hasConnectedRef.current && attemptRef.current > 0;
      attemptRef.current = 0;
      hasConnectedRef.current = true;
      setStatus('connected');

      if (wasReconnecting) {
        // F-603: Reconnection successful — send SESSION_REJOIN if we have an active session
        setIsReconnecting(false);
        setJustReconnected(true);

        // Auto-dismiss the "Reconnected" toast after 3 seconds
        if (reconnectedToastTimerRef.current) {
          clearTimeout(reconnectedToastTimerRef.current);
        }
        reconnectedToastTimerRef.current = setTimeout(() => {
          setJustReconnected(false);
          reconnectedToastTimerRef.current = null;
        }, 3_000);

        // Send SESSION_REJOIN if we have an active session
        const currentSessionId = sessionIdRef.current;
        if (currentSessionId) {
          // Small delay to ensure the WebSocket is fully ready before sending
          setTimeout(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'SESSION_REJOIN', sessionId: currentSessionId }));
            }
          }, 50);
        }
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      let msg: WSInboundMessage;
      try {
        msg = JSON.parse(event.data as string) as WSInboundMessage;
      } catch {
        return; // Ignore non-JSON (binary handled elsewhere)
      }

      // Transcript catch-up: on SESSION_REJOINED, fetch any segments missed during disconnect
      if (msg.type === 'SESSION_REJOINED') {
        const serverSeq = typeof msg['lastDeliveredSeqNumber'] === 'number' ? msg['lastDeliveredSeqNumber'] : 0;
        const clientSeq = lastSeqNumberRef.current;
        const sid = sessionIdRef.current;
        if (serverSeq > clientSeq && sid) {
          // Fetch missed segments via REST
          const apiBase = import.meta.env.VITE_API_URL ?? '';
          fetch(`${apiBase}/api/sessions/${sid}/transcript?after=${clientSeq}`, {
            headers: { Authorization: `Bearer ${tokenRef.current ?? ''}` },
          })
            .then((r) => r.json() as Promise<{ segments: TranscriptSegmentCatchUp[] }>)
            .then(({ segments }) => {
              if (segments && segments.length > 0) {
                setMissedSegments(segments);
              }
            })
            .catch((err) => {
              console.warn('[WebSocketContext] Transcript catch-up fetch failed:', err);
            });
        }
      }

      const handlers = handlersRef.current.get(msg.type);
      if (handlers) {
        handlers.forEach((h) => h(msg));
      }
      // Also dispatch to wildcard handlers
      const wildcardHandlers = handlersRef.current.get('*');
      if (wildcardHandlers) {
        wildcardHandlers.forEach((h) => h(msg));
      }
    };

    ws.onclose = () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      wsRef.current = null;
      setRawSocket(null);
      setStatus('disconnected');

      if (shouldReconnectRef.current && tokenRef.current) {
        // Mark as reconnecting if we had previously connected
        if (hasConnectedRef.current) {
          setIsReconnecting(true);
        }
        const delay = backoffDelay(attemptRef.current);
        attemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          if (shouldReconnectRef.current && tokenRef.current) {
            openSocket(tokenRef.current);
          }
        }, delay);
      }
    };

    ws.onerror = () => {
      // onclose will fire after onerror — reconnect logic lives there
    };
  }, []);

  // -------------------------------------------------------------------------
  // connect — called externally after successful auth
  // -------------------------------------------------------------------------
  const connect = useCallback(
    (token: string) => {
      tokenRef.current = token;
      shouldReconnectRef.current = true;
      attemptRef.current = 0;
      hasConnectedRef.current = false; // reset so first connection doesn't show "Reconnected"

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      openSocket(token);
    },
    [openSocket],
  );

  // -------------------------------------------------------------------------
  // disconnect — called on logout
  // -------------------------------------------------------------------------
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    tokenRef.current = null;
    hasConnectedRef.current = false;
    sessionIdRef.current = null;
    lastSeqNumberRef.current = 0;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (reconnectedToastTimerRef.current) {
      clearTimeout(reconnectedToastTimerRef.current);
      reconnectedToastTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setRawSocket(null);
    setStatus('disconnected');
    setIsReconnecting(false);
    setJustReconnected(false);
  }, []);

  // -------------------------------------------------------------------------
  // setSessionId — called by session pages so SESSION_REJOIN can include it
  // -------------------------------------------------------------------------
  const setSessionId = useCallback((sessionId: string | null) => {
    sessionIdRef.current = sessionId;
  }, []);

  // -------------------------------------------------------------------------
  // updateLastSeqNumber — called by session pages as segments arrive
  // -------------------------------------------------------------------------
  const updateLastSeqNumber = useCallback((seq: number) => {
    if (seq > lastSeqNumberRef.current) {
      lastSeqNumberRef.current = seq;
    }
  }, []);

  // -------------------------------------------------------------------------
  // clearMissedSegments — called by session page after consuming missed segments
  // -------------------------------------------------------------------------
  const clearMissedSegments = useCallback(() => {
    setMissedSegments([]);
  }, []);

  // -------------------------------------------------------------------------
  // Cleanup on unmount
  // -------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (reconnectedToastTimerRef.current) clearTimeout(reconnectedToastTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, []);

  // -------------------------------------------------------------------------
  // send
  // -------------------------------------------------------------------------
  const send = useCallback((message: WSOutboundMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocketContext] send() called while not connected', message.type);
    }
  }, []);

  // -------------------------------------------------------------------------
  // onMessage subscription
  // -------------------------------------------------------------------------
  const onMessage = useCallback((type: string, handler: MessageHandler): (() => void) => {
    if (!handlersRef.current.has(type)) {
      handlersRef.current.set(type, new Set());
    }
    handlersRef.current.get(type)!.add(handler);

    return () => {
      handlersRef.current.get(type)?.delete(handler);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------
  const value = useMemo<WebSocketContextValue>(
    () => ({
      status,
      isReconnecting,
      justReconnected,
      rawSocket,
      send,
      onMessage,
      connect,
      disconnect,
      setSessionId,
      updateLastSeqNumber,
      missedSegments,
      clearMissedSegments,
    }),
    [status, isReconnecting, justReconnected, rawSocket, send, onMessage, connect, disconnect, setSessionId, updateLastSeqNumber, missedSegments, clearMissedSegments],
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return ctx;
}
